import React, { Suspense, lazy, useEffect, useState } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { sessionService } from 'utils/sessionService'
import { loginSuccess, getUserDataSuccess, getNetwork, logout } from 'store/modules/auth'
import { adminSelectNetwork } from 'store/modules/networks'
import { closeSidebar } from 'store/modules/site'

// Layout
import PageLayout from 'layouts/PageLayout/PageLayout'

// Loading component
const Loading = () => <div style={{ padding: 20 }}>Loading...</div>

// Auth wrapper component for protected routes
const RequireAuth = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const location = useLocation()
  const auth = useSelector(state => state.auth)
  const dispatch = useDispatch()

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const [session, user] = await Promise.all([
          sessionService.loadSession().catch(() => null),
          sessionService.loadUser().catch(() => null)
        ])

        if (session && user) {
          dispatch(loginSuccess(session))
          dispatch(getUserDataSuccess(user))

          if (session.adminClientId && user.userType === 'Admin') {
            dispatch(adminSelectNetwork(session.adminClientId))
          } else if (user.clientRestricted && user.accountClients?.[0]?.clientId) {
            dispatch(adminSelectNetwork(user.accountClients[0].clientId))
            dispatch(getNetwork(user.accountClients[0].clientId))
          } else if (user.userClientId && user.userClientId !== 0) {
            if (user.userType === 'Admin') {
              dispatch(adminSelectNetwork(user.userClientId))
            }
            dispatch(getNetwork(user.userClientId))
          }

          setIsAuthenticated(true)
        } else {
          if (!auth.account?.accessToken) {
            dispatch(logout())
            dispatch(closeSidebar())
            setIsAuthenticated(false)
          } else {
            setIsAuthenticated(true)
          }
        }
      } catch (err) {
        setIsAuthenticated(false)
      }
      setIsChecking(false)
    }

    checkAuth()
  }, [dispatch, auth.account?.accessToken])

  if (isChecking) {
    return <Loading />
  }

  if (!isAuthenticated) {
    return <Navigate to='/login' state={{ from: location }} replace />
  }

  return children
}

RequireAuth.propTypes = {
  children: PropTypes.node.isRequired
}

// Lazy load route components - using actual container names
const LoginContainer = lazy(() => import('routes/Login/containers/LoginContainer'))
const DashboardContainer = lazy(() => import('routes/Dashboard/containers/DashboardContainer'))
const AssetsContainer = lazy(() => import('routes/Assets/containers/AssetsContainer'))
const NetworksContainer = lazy(() => import('routes/Networks/containers/NetworksContainer'))
const NetworkReportsContainer = lazy(() => import('routes/NetworkReports/containers/NetworkReports'))
const ManholesContainer = lazy(() => import('routes/Manholes/containers/Manholes'))
const ManholesViewContainer = lazy(() => import('routes/Manholes/containers/ManholesViewContainer'))
const SparesContainer = lazy(() => import('routes/Spares/containers/SparesContainer'))
const SparesViewContainer = lazy(() => import('routes/Spares/containers/SparesViewContainer'))
const SparesAuditsContainer = lazy(() => import('routes/Spares/containers/SparesAuditsContainer'))
const SparesViewWrapper = lazy(() => import('routes/Spares/containers/SparesViewWrapper'))
const SparesEditWrapper = lazy(() => import('routes/Spares/containers/SparesEditWrapper'))
const SparesAuditWrapper = lazy(() => import('routes/Spares/containers/SparesAuditWrapper'))
const RoutesContainer = lazy(() => import('routes/Routes/containers/RoutesContainer'))
const AccountsContainer = lazy(() => import('routes/Accounts/containers/AccountsContainer'))
const UtilityRequestsContainer = lazy(() => import('routes/UtilityRequests/containers/UtilityRequestsContainer'))
const PlannedWorksContainer = lazy(() => import('routes/PlannedWorks/containers/PlannedWorksContainer'))
const RectificationWorksContainer = lazy(() => {
  return import('routes/RectificationWorks/containers/RectificationWorksContainer')
})
const SitesReportsContainer = lazy(() => import('routes/SitesReports/containers/SitesReportsContainer'))
const SiteReportsEditContainer = lazy(() => import('routes/SitesReports/containers/SiteReportsEditContainer'))
const LTAReportContainer = lazy(() => import('routes/LTAReport/containers/LTAReportContainer'))
const ManholeSearchContainer = lazy(() => import('routes/Search/containers/ManholeSearchContainer'))
const ConstructionOnNetworkContainer = lazy(() => {
  return import('routes/ConstructionOnNetwork/containers/ConstructionOnNetworkContainer')
})
const ConstructionOnNetworkViewContainer = lazy(() => {
  return import('routes/ConstructionOnNetwork/containers/ConstructionOnNetworkViewContainer')
})
const ConstructionOnNetworkEditContainer = lazy(() => {
  return import('routes/ConstructionOnNetwork/containers/ConstructionOnNetworkEditContainer')
})
const ConstructionOnNetworkCreateContainer = lazy(() => {
  return import('routes/ConstructionOnNetwork/containers/ConstructionOnNetworkCreateContainer')
})
const DocumentsContainer = lazy(() => import('routes/DocumentManagement/containers/DocumentsContainer'))
const SiteAuditContainer = lazy(() => import('routes/SiteAudit/containers/SiteAuditContainer'))
const SiteAuditViewContainer = lazy(() => import('routes/SiteAudit/containers/SiteAuditViewContainer'))
const AppDownloadContainer = lazy(() => import('routes/AppDownload/containers/AppDownloadContainer'))
const AuditorMapContainer = lazy(() => import('routes/Accounts/containers/AuditorMapContainer'))
const AssetAuditEditContainer = lazy(() => import('routes/NetworkReports/containers/AssetAuditEditContainer'))
const AssetCreateWrapper = lazy(() => import('routes/Assets/containers/AssetCreateWrapper'))
const NceReportsContainer = lazy(() => import('routes/NceReports/containers/NceReportsContainer'))
const NceReportsViewContainer = lazy(() => import('routes/NceReports/containers/NceReportsViewContainer'))

const AppRoutes = () => {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* Public routes */}
        <Route path='/login' element={<LoginContainer />} />
        <Route path='/login/reset' element={<LoginContainer />} />
        <Route path='/login/change/:userId/:token' element={<LoginContainer />} />

        {/* Protected routes */}
        <Route path='/' element={<PageLayout />}>
          <Route index element={<Navigate to='/content/dashboard' replace />} />

          {/* Dashboard */}
          <Route
            path='content/dashboard'
            element={
              <RequireAuth>
                <DashboardContainer />
              </RequireAuth>
            }
          />
          <Route
            path='content/dashboard/index/:month/:year'
            element={
              <RequireAuth>
                <DashboardContainer />
              </RequireAuth>
            }
          />

          {/* Assets */}
          <Route
            path='content/assets'
            element={
              <RequireAuth>
                <AssetsContainer />
              </RequireAuth>
            }
          />

          {/* Networks */}
          <Route
            path='content/networks'
            element={
              <RequireAuth>
                <NetworksContainer />
              </RequireAuth>
            }
          />

          {/* Network Reports */}
          <Route
            path='content/network-reports'
            element={
              <RequireAuth>
                <NetworkReportsContainer />
              </RequireAuth>
            }
          />
          <Route
            path='content/report/get_network_report'
            element={
              <RequireAuth>
                <NetworkReportsContainer />
              </RequireAuth>
            }
          />
          <Route
            path='content/report/get_network_report/:month/:year'
            element={
              <RequireAuth>
                <NetworkReportsContainer />
              </RequireAuth>
            }
          />

          {/* Manholes */}
          <Route
            path='content/manholes'
            element={
              <RequireAuth>
                <ManholesContainer />
              </RequireAuth>
            }
          />
          <Route
            path='content/manholes/:id'
            element={
              <RequireAuth>
                <ManholesViewContainer />
              </RequireAuth>
            }
          />

          {/* Spares */}
          <Route
            path='content/spares'
            element={
              <RequireAuth>
                <SparesContainer />
              </RequireAuth>
            }
          />
          <Route
            path='content/spares/:id'
            element={
              <RequireAuth>
                <SparesViewContainer />
              </RequireAuth>
            }
          />
          <Route
            path='content/spares/audits/:id'
            element={
              <RequireAuth>
                <SparesAuditsContainer />
              </RequireAuth>
            }
          />

          {/* Routes */}
          <Route
            path='content/routes'
            element={
              <RequireAuth>
                <RoutesContainer />
              </RequireAuth>
            }
          />

          {/* Accounts */}
          <Route
            path='content/accounts'
            element={
              <RequireAuth>
                <AccountsContainer />
              </RequireAuth>
            }
          />

          {/* Utility Requests */}
          <Route
            path='content/utility-requests'
            element={
              <RequireAuth>
                <UtilityRequestsContainer />
              </RequireAuth>
            }
          />

          {/* Planned Works */}
          <Route
            path='content/planned-works'
            element={
              <RequireAuth>
                <PlannedWorksContainer />
              </RequireAuth>
            }
          />

          {/* Rectification Works */}
          <Route
            path='content/rectification-works'
            element={
              <RequireAuth>
                <RectificationWorksContainer />
              </RequireAuth>
            }
          />

          {/* Sites Reports */}
          <Route
            path='content/sites-reports'
            element={
              <RequireAuth>
                <SitesReportsContainer />
              </RequireAuth>
            }
          />

          {/* LTA Report */}
          <Route
            path='content/lta-report'
            element={
              <RequireAuth>
                <LTAReportContainer />
              </RequireAuth>
            }
          />

          {/* Search */}
          <Route
            path='content/search'
            element={
              <RequireAuth>
                <ManholeSearchContainer />
              </RequireAuth>
            }
          />
          <Route
            path='content/search/:searchtext'
            element={
              <RequireAuth>
                <ManholeSearchContainer />
              </RequireAuth>
            }
          />

          {/* Construction on Network */}
          <Route
            path='content/construction-on-network'
            element={
              <RequireAuth>
                <ConstructionOnNetworkContainer />
              </RequireAuth>
            }
          />
          <Route
            path='content/construction-on-network/:id'
            element={
              <RequireAuth>
                <ConstructionOnNetworkViewContainer />
              </RequireAuth>
            }
          />

          {/* Document Management */}
          <Route
            path='content/document-management'
            element={
              <RequireAuth>
                <DocumentsContainer />
              </RequireAuth>
            }
          />

          {/* Site Audit */}
          <Route
            path='content/site-audit'
            element={
              <RequireAuth>
                <SiteAuditContainer />
              </RequireAuth>
            }
          />
          <Route
            path='content/site-audit/view/:id'
            element={
              <RequireAuth>
                <SiteAuditViewContainer />
              </RequireAuth>
            }
          />

          {/* App Download */}
          <Route
            path='content/app-download'
            element={
              <RequireAuth>
                <AppDownloadContainer />
              </RequireAuth>
            }
          />

          {/* ============================================ */}
          {/* Legacy route aliases for Sidebar compatibility */}
          {/* ============================================ */}

          {/* Assets - legacy singular route */}
          <Route
            path='content/asset'
            element={
              <RequireAuth>
                <AssetsContainer />
              </RequireAuth>
            }
          />
          <Route
            path='content/asset/create'
            element={
              <RequireAuth>
                <AssetCreateWrapper />
              </RequireAuth>
            }
          />
          <Route
            path='content/asset/:type/:id'
            element={
              <RequireAuth>
                <AssetCreateWrapper />
              </RequireAuth>
            }
          />

          {/* Networks - legacy singular route */}
          <Route
            path='content/network'
            element={
              <RequireAuth>
                <NetworksContainer />
              </RequireAuth>
            }
          />

          {/* Utility Requests - legacy route */}
          <Route
            path='content/utility'
            element={
              <RequireAuth>
                <UtilityRequestsContainer />
              </RequireAuth>
            }
          />

          {/* Planned Works - legacy route */}
          <Route
            path='content/npw'
            element={
              <RequireAuth>
                <PlannedWorksContainer />
              </RequireAuth>
            }
          />

          {/* Documents - legacy route */}
          <Route
            path='content/documents'
            element={
              <RequireAuth>
                <DocumentsContainer />
              </RequireAuth>
            }
          />

          {/* Construction on Network - legacy underscore route */}
          <Route
            path='content/construction_on_network'
            element={
              <RequireAuth>
                <ConstructionOnNetworkContainer />
              </RequireAuth>
            }
          />
          <Route
            path='content/construction_on_network/view/:id'
            element={
              <RequireAuth>
                <ConstructionOnNetworkViewContainer />
              </RequireAuth>
            }
          />
          <Route
            path='content/construction_on_network/edit/:id'
            element={
              <RequireAuth>
                <ConstructionOnNetworkEditContainer />
              </RequireAuth>
            }
          />
          <Route
            path='content/construction_on_network/create'
            element={
              <RequireAuth>
                <ConstructionOnNetworkCreateContainer />
              </RequireAuth>
            }
          />

          {/* Rectification Works with type parameter */}
          <Route
            path='content/report/get_rectification_works/:type'
            element={
              <RequireAuth>
                <RectificationWorksContainer />
              </RequireAuth>
            }
          />

          {/* Service Requests / Incidents with type parameter */}
          <Route
            path='content/service_request/get_requests/:type'
            element={
              <RequireAuth>
                <SitesReportsContainer />
              </RequireAuth>
            }
          />
          <Route
            path='content/service_request/edit/:id'
            element={
              <RequireAuth>
                <SiteReportsEditContainer />
              </RequireAuth>
            }
          />

          {/* Manholes Under Construction (MUC) */}
          <Route
            path='content/muc/:type'
            element={
              <RequireAuth>
                <SitesReportsContainer />
              </RequireAuth>
            }
          />

          {/* Accounts with type parameter */}
          <Route
            path='content/accounts/:type'
            element={
              <RequireAuth>
                <AccountsContainer />
              </RequireAuth>
            }
          />

          {/* Auditor Map */}
          <Route
            path='content/accounts/map'
            element={
              <RequireAuth>
                <AuditorMapContainer />
              </RequireAuth>
            }
          />

          {/* Spares Audits list (without id) */}
          <Route
            path='content/spares/audits'
            element={
              <RequireAuth>
                <SparesAuditsContainer />
              </RequireAuth>
            }
          />

          {/* Spares View - legacy route */}
          <Route
            path='content/spares/get_spare_view/:spareId'
            element={
              <RequireAuth>
                <SparesViewWrapper />
              </RequireAuth>
            }
          />

          {/* Spares Edit - legacy route */}
          <Route
            path='content/spares/get_spare_edit/:spareId'
            element={
              <RequireAuth>
                <SparesEditWrapper />
              </RequireAuth>
            }
          />

          {/* Spares Audit - legacy route */}
          <Route
            path='content/spares/audit/:spareId'
            element={
              <RequireAuth>
                <SparesAuditWrapper />
              </RequireAuth>
            }
          />

          {/* Asset Audit Edit */}
          <Route
            path='content/report/edit_audit/audit/:id'
            element={
              <RequireAuth>
                <AssetAuditEditContainer />
              </RequireAuth>
            }
          />

          {/* NCE Reports */}
          <Route
            path='content/nce_report/:type'
            element={
              <RequireAuth>
                <NceReportsContainer />
              </RequireAuth>
            }
          />
          <Route
            path='content/nce_report/:type/get_more/:nceId'
            element={
              <RequireAuth>
                <NceReportsViewContainer />
              </RequireAuth>
            }
          />

          {/* LTA Report - legacy route */}
          <Route
            path='content/ltareport'
            element={
              <RequireAuth>
                <LTAReportContainer />
              </RequireAuth>
            }
          />
        </Route>

        {/* Catch all - redirect to dashboard */}
        <Route path='*' element={<Navigate to='/content/dashboard' replace />} />
      </Routes>
    </Suspense>
  )
}

AppRoutes.propTypes = {}

export default AppRoutes
